import React, { useState, useEffect, useCallback, memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, Download, Trash2, Edit, Calendar as CalendarIcon, Loader2, ShoppingCart, DollarSign, Landmark, Percent, Check } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AdvancedCalendar } from '@/components/ui/advanced-calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PaginationControls from '@/components/PaginationControls';

const SalesTable = ({
  fetchSales,
  fetchSalesSummary,
  onEditSale,
  onDeleteSale,
  onDeleteSalesBatch,
  onDeleteAllSales,
  operators,
  flags,
  paymentMethods,
  sharedFilters,
  refreshTrigger
}) => {
  const { toast } = useToast();

  // -- State Management --
  const [sales, setSales] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Paginação menor para ser instantâneo

  // -- Summary Cards State --
  const [summary, setSummary] = useState({
    totalVendas: 0,
    totalRecebido: 0,
    vendasQuitadas: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(false);

  // -- Shared Filters Logic --
  const searchTerm = sharedFilters?.searchTerm || '';
  const setSearchTerm = sharedFilters?.updateSearchTerm || (() => { });
  const selectedOperators = sharedFilters?.selectedOperators || new Set();
  const setSelectedOperators = sharedFilters?.updateSelectedOperators || (() => { });
  const selectedFlags = sharedFilters?.selectedFlags || new Set();
  const setSelectedFlags = sharedFilters?.updateSelectedFlags || (() => { });
  const selectedPaymentMethods = sharedFilters?.selectedPaymentMethods || new Set();
  const setSelectedPaymentMethods = sharedFilters?.updateSelectedPaymentMethods || (() => { });
  const dateRange = sharedFilters?.dateRange || { startDate: null, endDate: null };
  const setDateRange = sharedFilters?.updateDateRange || (() => { });

  // Local Filters
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedSales, setSelectedSales] = useState(new Set());

  // -- Debounce Search --
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // -- Fetch Logic --
  const loadSales = useCallback(async () => {
    // Se a função não estiver disponível, não fazer nada (evita o erro "not a function")
    if (!fetchSales) return;

    setLoading(true);
    const filters = {
      searchTerm: debouncedSearchTerm,
      dateRange,
      selectedOperators,
      selectedFlags,
      selectedPaymentMethods,
      selectedStatuses
    };

    try {
      const { data, count } = await fetchSales({
        page: currentPage,
        pageSize,
        filters
      });

      setSales(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error("Erro ao carregar vendas:", error);
      toast({ title: "Erro", description: "Falha ao carregar vendas.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [fetchSales, currentPage, pageSize, debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods, selectedStatuses, refreshTrigger, toast]);

  // -- Summary Logic --
  const loadSummary = useCallback(async () => {
    if (!fetchSalesSummary) return;

    setLoadingSummary(true);
    const filters = {
      searchTerm: debouncedSearchTerm,
      dateRange,
      selectedOperators,
      selectedFlags,
      selectedPaymentMethods,
      selectedStatuses
    };

    try {
      const result = await fetchSalesSummary({ filters });
      setSummary(result);
    } catch (error) {
      console.error("Erro ao carregar resumo:", error);
    } finally {
      setLoadingSummary(false);
    }
  }, [fetchSalesSummary, debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods, selectedStatuses, refreshTrigger]);

  // -- Effects --
  // 1. Load data on mount and when dependencies change
  useEffect(() => {
    loadSales();
  }, [loadSales]);

  // 2. Load summary when filters change (but not page)
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // 3. Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, dateRange, selectedOperators, selectedFlags, selectedPaymentMethods, selectedStatuses]);


  // -- Handlers --
  const handleSelectSale = (saleId) => {
    setSelectedSales(prev => {
      const newSet = new Set(prev);
      if (newSet.has(saleId)) newSet.delete(saleId);
      else newSet.add(saleId);
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSales.size === sales.length) setSelectedSales(new Set());
    else setSelectedSales(new Set(sales.map(s => s.id)));
  };

  const handleDeleteBatch = async () => {
    await onDeleteSalesBatch(Array.from(selectedSales));
    setSelectedSales(new Set());
    loadSales(); // Reload after delete
    loadSummary();
  };

  const hasActiveFilters = Boolean(
    debouncedSearchTerm || 
    dateRange.startDate || 
    dateRange.endDate || 
    selectedOperators.size > 0 || 
    selectedFlags.size > 0 || 
    selectedPaymentMethods.size > 0 || 
    selectedStatuses.size > 0
  );

  const handleDeleteFiltered = async () => {
    toast({ title: "Excluindo vendas...", description: "Isso pode levar alguns segundos dependendo da quantidade." });
    
    const filters = {
      searchTerm: debouncedSearchTerm,
      dateRange,
      selectedOperators,
      selectedFlags,
      selectedPaymentMethods,
      selectedStatuses,
      fetchAll: true
    };

    try {
      // Usar página 1 e um tamanho gigantesco para pegar todos os IDs do filtro
      const { data } = await fetchSales({ page: 1, pageSize: 99999, filters });
      
      if (!data || data.length === 0) {
        toast({ title: "Sem dados", description: "Nenhuma venda encontrada para excluir.", variant: "destructive" });
        return;
      }
      
      const idsToDelete = data.map(sale => sale.id);
      await onDeleteSalesBatch(idsToDelete);
      
      setSelectedSales(new Set());
      loadSales();
      loadSummary();
      toast({ title: "Sucesso!", description: `${idsToDelete.length} vendas foram excluídas.` });
    } catch (error) {
       console.error("Erro ao excluir vendas filtradas", error);
       toast({ title: "Erro", description: "Falha ao excluir as vendas.", variant: "destructive" });
    }
  };

  const handleExport = async () => {
    // Fetch ALL matching rows for export, not just current page
    // Warning: This might be slow for massive datasets
    toast({ title: "Gerando PDF...", description: "Isso pode levar alguns segundos." });

    const filters = {
      searchTerm: debouncedSearchTerm,
      dateRange,
      selectedOperators,
      selectedFlags,
      selectedPaymentMethods,
      selectedStatuses
    };

    // Use a large page size to try to get everything (or pagination loop if needed)
    // For simplicity, we fetch up to 2000 rows for export
    const { data } = await fetchSales({ page: 1, pageSize: 2000, filters });

    if (!data || data.length === 0) {
      toast({ title: "Sem dados", description: "Nenhuma venda encontrada para exportar.", variant: "destructive" });
      return;
    }

    const doc = new jsPDF('landscape');
    doc.text("Relatório de Vendas", 14, 16);

    const head = [[
      "Data", "Operadora", "Bandeira", "Tipo", "Parcelas",
      "Valor", "Recebido", "% Taxa", "Cód. Auto", "Status"
    ]];

    const body = data.map(sale => [
      formatDate(sale.data),
      sale.empresa,
      sale.bandeira,
      sale.tipotransacao,
      `${sale.parcelas}`,
      formatCurrency(sale.valorcompra),
      formatCurrency(sale.valorrecebido),
      `${sale.valorcompra > 0 ? (((sale.valorcompra - sale.valorrecebido) / sale.valorcompra) * 100).toFixed(2) : 0}%`,
      sale.codigoautorizacao || '',
      sale.status
    ]);

    doc.autoTable({
      head: head,
      body: body,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 163, 74] },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 9) { // Coluna 'Status' é a 10ª (índice 9)
          const status = data.cell.raw;
          if (status === 'Quitado') {
             data.cell.styles.textColor = [22, 163, 74]; // text-green-600
             data.cell.styles.fontStyle = 'bold';
          } else {
             data.cell.styles.textColor = [220, 38, 38]; // text-red-600
             data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    doc.save(`vendas_export_${new Date().toISOString().split('T')[0]}.pdf`);
    toast({ title: "Sucesso", description: "PDF gerado com sucesso." });
  };

  // -- Render Helpers --
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString.replace(/-/g, '/')).toLocaleDateString('pt-BR');
  };
  const getStatusBadgeClass = (status) => status === 'Quitado' ? 'status-quitado' : 'status-aberto';

  const resetFilters = () => {
    setDateRange({ startDate: null, endDate: null });
    setSearchTerm('');
    setSelectedOperators(new Set());
    setSelectedStatuses(new Set());
    setSelectedFlags(new Set());
    setSelectedPaymentMethods(new Set());
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const operatorOptions = operators.map(op => ({ value: op.name, label: op.name }));
  const flagOptions = flags.map(f => ({ value: f.name, label: f.name }));
  const paymentMethodOptions = paymentMethods.map(pm => ({ value: pm.name, label: pm.name }));
  const statusOptions = [{ value: 'Quitado', label: 'Quitado' }, { value: 'Em Aberto', label: 'Em Aberto' }];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { title: "Total de Vendas", value: summary.count, color: "text-white", border: "border-l-4 border-l-[#00D26A]", iconBg: "bg-[#00D26A]/20", iconText: "text-[#00D26A]", shadowColor: "#00D26A", Icon: ShoppingCart },
          { title: "Valor Total", value: formatCurrency(summary.totalVendas), color: "text-white", border: "border-l-4 border-l-[#0084FF]", iconBg: "bg-[#0084FF]/20", iconText: "text-[#0084FF]", shadowColor: "#0084FF", Icon: DollarSign },
          { title: "Total Recebido", value: formatCurrency(summary.totalRecebido), color: "text-[#0084FF]", border: "border-l-4 border-l-[#7B2CBF]", iconBg: "bg-[#7B2CBF]/20", iconText: "text-[#7B2CBF]", shadowColor: "#7B2CBF", Icon: Landmark },
          { title: "% Taxa", value: `${summary.totalVendas > 0 ? (((summary.totalVendas - summary.totalRecebido) / summary.totalVendas) * 100).toFixed(2) : '0.00'}%`, color: "text-[#FF7A00]", border: "border-l-4 border-l-[#FF7A00]", iconBg: "bg-[#FF7A00]/20", iconText: "text-[#FF7A00]", shadowColor: "#FF7A00", Icon: Percent },
          { title: "Vendas Quitadas", value: summary.vendasQuitadas, color: "text-[#9D4EDD]", border: "border-l-4 border-l-[#9D4EDD]", iconBg: "bg-[#9D4EDD]/20", iconText: "text-[#9D4EDD]", shadowColor: "#9D4EDD", Icon: Check }
        ].map((card, idx) => (
          <Card key={idx} className={`glass-effect border-white/10 ${card.border} relative overflow-hidden`}>
            <div 
              className="absolute left-0 top-0 bottom-0 w-1"
              style={{ backgroundColor: card.shadowColor, boxShadow: `0 0 15px ${card.shadowColor}` }}
            ></div>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${card.iconBg} ${card.iconText}`}>
                <card.Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm font-medium">{card.title}</p>
                {loadingSummary ? <Loader2 className="w-4 h-4 animate-spin mt-1 text-white/50" /> : <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="glass-effect border-white/20">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Filter className="w-5 h-5" />Filtros e Busca</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="relative md:col-span-2 lg:col-span-4">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white" />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={"outline"} className={cn("w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white hover:text-white hover:bg-white/20", !dateRange.startDate && "text-white/50")}><CalendarIcon className="mr-2 h-4 w-4" />{dateRange.startDate ? format(dateRange.startDate, "dd/MM/yyyy") : <span>Data inicial</span>}</Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><AdvancedCalendar mode="single" selected={dateRange.startDate} onSelect={(date) => setDateRange(prev => ({ ...prev, startDate: date }))} initialFocus /></PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={"outline"} className={cn("w-full justify-start text-left font-normal bg-white/10 border-white/20 text-white hover:text-white hover:bg-white/20", !dateRange.endDate && "text-white/50")}><CalendarIcon className="mr-2 h-4 w-4" />{dateRange.endDate ? format(dateRange.endDate, "dd/MM/yyyy") : <span>Data final</span>}</Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><AdvancedCalendar mode="single" selected={dateRange.endDate} onSelect={(date) => setDateRange(prev => ({ ...prev, endDate: date }))} initialFocus /></PopoverContent>
            </Popover>

            <MultiSelectFilter title="Operadoras" options={operatorOptions} selected={selectedOperators} onSelectedChange={setSelectedOperators} />
            <MultiSelectFilter title="Bandeiras" options={flagOptions} selected={selectedFlags} onSelectedChange={setSelectedFlags} />
            <MultiSelectFilter title="Formas Pagto" options={paymentMethodOptions} selected={selectedPaymentMethods} onSelectedChange={setSelectedPaymentMethods} />
            <MultiSelectFilter title="Status" options={statusOptions} selected={selectedStatuses} onSelectedChange={setSelectedStatuses} />

            <Button onClick={resetFilters} variant="ghost" className="text-white/70 hover:text-white md:col-start-3">Limpar</Button>
            <Button onClick={handleExport} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"><Download className="w-4 h-4 mr-2" />PDF</Button>
          </div>
        </CardContent>
      </Card>

      <PaginationControls
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / pageSize)}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        itemLabel="vendas"
        onDeleteAll={onDeleteAllSales}
        hasActiveFilters={hasActiveFilters}
        onDeleteFiltered={handleDeleteFiltered}
      />

      {/* Table */}
      <Card className="glass-effect border-white/20">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Vendas</CardTitle>
            {selectedSales.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="destructive"><Trash2 className="w-4 h-4 mr-2" /> Deletar ({selectedSales.size})</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Confirmar exclusão?</AlertDialogTitle><AlertDialogDescription>Excluir {selectedSales.size} vendas selecionadas permanentemente?</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDeleteBatch}>Deletar</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-white/70"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />Carregando vendas...</div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12 text-white/70">Nenhuma venda encontrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="p-3"><Checkbox checked={sales.length > 0 && selectedSales.size === sales.length} onCheckedChange={handleSelectAll} /></th>
                    <th className="text-left p-3 text-white/80">Data</th>
                    <th className="text-left p-3 text-white/80">Operadora</th>
                    <th className="text-left p-3 text-white/80">Bandeira</th>
                    <th className="text-left p-3 text-white/80">Tipo</th>
                    <th className="text-left p-3 text-white/80">Parcelas</th>
                    <th className="text-left p-3 text-white/80">Valor</th>
                    <th className="text-left p-3 text-white/80">Recebido</th>
                    <th className="text-left p-3 text-white/80">% Taxa</th>
                    <th className="text-left p-3 text-white/80">Cód. Auto</th>
                    <th className="text-left p-3 text-white/80">Cód. Pedido</th>
                    <th className="text-left p-3 text-white/80">Status</th>
                    <th className="text-left p-3 text-white/80">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b border-white/10 hover:bg-white/5">
                      <td className="p-3"><Checkbox checked={selectedSales.has(sale.id)} onCheckedChange={() => handleSelectSale(sale.id)} /></td>
                      <td className="p-3 text-white text-sm">{formatDate(sale.data)}</td>
                      <td className="p-3 text-white text-sm">{sale.empresa}</td>
                      <td className="p-3 text-white text-sm">{sale.bandeira}</td>
                      <td className="p-3 text-white text-sm capitalize">{sale.tipotransacao}</td>
                      <td className="p-3 text-white text-sm">{sale.parcelas || '-'}</td>
                      <td className="p-3 text-white text-sm">{formatCurrency(sale.valorcompra)}</td>
                      <td className="p-3 text-white text-sm">{formatCurrency(sale.valorrecebido)}</td>
                      <td className="p-3 text-white text-sm">{sale.valorcompra > 0 ? (((sale.valorcompra - sale.valorrecebido) / sale.valorcompra) * 100).toFixed(2) : 0}%</td>
                      <td className="p-3 text-white text-sm">{sale.codigoautorizacao || ''}</td>
                      <td className="p-3 text-white text-sm">{sale.codigopedido || ''}</td>
                      <td className="p-3"><Badge className={getStatusBadgeClass(sale.status)}>{sale.status}</Badge></td>
                      <td className="p-3 flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => onEditSale(sale)} className="text-blue-400 hover:text-blue-300"><Edit className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Excluir venda?</AlertDialogTitle></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Não</AlertDialogCancel><AlertDialogAction onClick={() => { onDeleteSale(sale.id); loadSales(); loadSummary(); }}>Sim</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <PaginationControls
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / pageSize)}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        itemLabel="vendas"
        onDeleteAll={onDeleteAllSales}
      />
    </div>
  );
}; // Removed memo

export default SalesTable;
